import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Container,
  Tabs,
  Tab,
  Fade,
  useTheme,
  alpha
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import LocalAuth from '../components/Auth/LocalAuth';
// OAuth functionaliteit is tijdelijk uitgeschakeld - zal later weer worden geactiveerd voor Google & Microsoft login
// import OAuthLogin from '../components/OAuth/OAuthLogin';

// Keyframe animaties voor waterstof achtergrond
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(10px) rotate(240deg); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px) scale(1); }
  50% { transform: translateY(-30px) translateX(15px) scale(1.1); }
`;

const bubble = keyframes`
  0% { transform: translateY(100vh) scale(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

// Styled components voor professionele uitstraling
const LoginContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(-45deg, 
    ${alpha(theme.palette.primary.main, 0.1)}, 
    ${alpha(theme.palette.secondary.main, 0.15)}, 
    ${alpha('#E3F2FD', 0.8)}, 
    ${alpha('#F1F8E9', 0.6)}
  )`,
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 15s ease infinite`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    opacity: 0.3
  }
}));

// Waterstof molecule componenten
const HydrogenMolecule = styled('div')(({ theme, size = 40, delay = 0, duration = 8 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  animation: `${float} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.6)}, ${alpha(theme.palette.primary.light, 0.4)})`,
  },
  '&::before': {
    width: size * 0.4,
    height: size * 0.4,
    top: 0,
    left: 0,
    boxShadow: `0 0 ${size * 0.2}px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  '&::after': {
    width: size * 0.4,
    height: size * 0.4,
    bottom: 0,
    right: 0,
    boxShadow: `0 0 ${size * 0.2}px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const FloatingParticle = styled('div')(({ theme, size = 20, delay = 0, duration = 12 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.4)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
  animation: `${floatSlow} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  boxShadow: `0 0 ${size}px ${alpha(theme.palette.secondary.main, 0.2)}`,
}));

const BubbleParticle = styled('div')(({ theme, size = 15, delay = 0, duration = 8 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle at 30% 30%, ${alpha('#ffffff', 0.8)}, ${alpha(theme.palette.primary.light, 0.3)})`,
  animation: `${bubble} ${duration}s linear infinite`,
  animationDelay: `${delay}s`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const EnergyOrb = styled('div')(({ theme, size = 60, delay = 0 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent 70%)`,
  animation: `${pulse} 4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const AnimatedBackground = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 0,
});

const LoginPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  backdropFilter: 'blur(15px)',
  background: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  position: 'relative',
  zIndex: 10
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  '& img': {
    width: '180px',
    height: 'auto',
    marginBottom: theme.spacing(1),
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
  }
}));

const BrandTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1)
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 400,
  letterSpacing: '0.5px'
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main
  }
}));

const Footer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: '50%',
  transform: 'translateX(-50%)',
  textAlign: 'center',
  zIndex: 10,
  '& img': {
    height: '20px',
    opacity: 0.7,
    marginLeft: theme.spacing(1)
  }
}));

// Echte logo's uit de public/images map
const dyconLogo = '/images/logo_dycon.svg';
const slimverbruikLogo = '/images/logo_slimverbruik.nl.svg';

const Login = () => {
  const [authMethod, setAuthMethod] = useState(0);
  const theme = useTheme();

  const handleAuthMethodChange = (event, newValue) => {
    setAuthMethod(newValue);
  };

  const handleSwitchToOAuth = () => {
    setAuthMethod(1);
  };

  const handleSwitchToLocal = () => {
    setAuthMethod(0);
  };

  return (
    <LoginContainer maxWidth={false}>
      {/* Geanimeerde waterstof achtergrond */}
      <AnimatedBackground>
        {/* Waterstof molecules */}
        <HydrogenMolecule style={{ top: '10%', left: '10%' }} size={50} delay={0} duration={10} />
        <HydrogenMolecule style={{ top: '20%', right: '15%' }} size={35} delay={2} duration={8} />
        <HydrogenMolecule style={{ bottom: '30%', left: '20%' }} size={40} delay={4} duration={12} />
        <HydrogenMolecule style={{ bottom: '15%', right: '10%' }} size={45} delay={1} duration={9} />
        <HydrogenMolecule style={{ top: '50%', left: '5%' }} size={30} delay={3} duration={11} />
        <HydrogenMolecule style={{ top: '70%', right: '25%' }} size={38} delay={5} duration={7} />

        {/* Floating particles */}
        <FloatingParticle style={{ top: '15%', left: '30%' }} size={25} delay={0} duration={15} />
        <FloatingParticle style={{ top: '25%', right: '30%' }} size={20} delay={2} duration={12} />
        <FloatingParticle style={{ bottom: '25%', left: '40%' }} size={18} delay={4} duration={14} />
        <FloatingParticle style={{ bottom: '35%', right: '35%' }} size={22} delay={1} duration={13} />
        <FloatingParticle style={{ top: '60%', left: '15%' }} size={16} delay={3} duration={16} />
        <FloatingParticle style={{ top: '40%', right: '20%' }} size={24} delay={5} duration={11} />

        {/* Bubble particles (elektrolyse bubbles) */}
        <BubbleParticle style={{ left: '20%' }} size={12} delay={0} duration={6} />
        <BubbleParticle style={{ left: '40%' }} size={15} delay={1} duration={8} />
        <BubbleParticle style={{ left: '60%' }} size={10} delay={2} duration={7} />
        <BubbleParticle style={{ left: '80%' }} size={14} delay={3} duration={9} />
        <BubbleParticle style={{ left: '30%' }} size={11} delay={4} duration={6} />
        <BubbleParticle style={{ left: '70%' }} size={13} delay={5} duration={8} />

        {/* Energy orbs */}
        <EnergyOrb style={{ top: '20%', left: '50%' }} size={80} delay={0} />
        <EnergyOrb style={{ bottom: '20%', right: '50%' }} size={100} delay={2} />
        <EnergyOrb style={{ top: '60%', left: '70%' }} size={60} delay={1} />
      </AnimatedBackground>

      <LoginPaper elevation={0}>
        <LogoContainer>
          <img
            src={dyconLogo}
            alt="Dycon Logo"
          />
          <BrandTitle variant="h4" component="h1">
            Axioniq
          </BrandTitle>
          <SubTitle variant="body1">
            Slimme energie-arbitrage voor datagedreven energie-infrastructuur
          </SubTitle>
        </LogoContainer>

        {/* OAuth tabs zijn tijdelijk uitgeschakeld - alleen lokaal account beschikbaar */}
        {/* OAuth functionaliteit (Google/Microsoft) wordt later weer geactiveerd */}
        {/*
        <StyledTabs 
          value={authMethod} 
          onChange={handleAuthMethodChange} 
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          <StyledTab label="Lokaal Account" />
          <StyledTab label="Google / Microsoft" />
        </StyledTabs>
        */}

        {/* Titel weggehaald - niet meer nodig */}
        {/*
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Lokaal Account Login
          </Typography>
        </Box>
        */}

        <Fade in={authMethod === 0} timeout={300}>
          <Box sx={{ display: authMethod === 0 ? 'block' : 'none' }}>
            <LocalAuth onSwitchToOAuth={handleSwitchToOAuth} />
          </Box>
        </Fade>

        {/* OAuth login component is tijdelijk uitgeschakeld */}
        {/*
        <Fade in={authMethod === 1} timeout={300}>
          <Box sx={{ display: authMethod === 1 ? 'block' : 'none' }}>
            <OAuthLogin onSwitchToLocal={handleSwitchToLocal} />
          </Box>
        </Fade>
        */}

        <Box sx={{ mt: 4, textAlign: 'center', pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Powered by
          </Typography>
          <img
            src={slimverbruikLogo}
            alt="Slimverbruik.nl"
            style={{ height: '24px', width: 'auto' }}
          />
        </Box>
      </LoginPaper>

      <Footer>
        <Typography variant="body2" color="text.secondary">
          © 2025 Dycon · Innovatieve energieoplossingen
        </Typography>
      </Footer>
    </LoginContainer>
  );
};

export default Login; 